import React, { useEffect, useState } from 'react'
import { connect } from "react-redux";
import Chart from "react-apexcharts"
import { ShipmentState, AppState, Dispatcher, RootState, ILocationListAllModel, IPump } from "AppTypes";
import { shipmentActions } from "../../shipment/registration/state_management/actions";
import { appActions } from "../../../app/state_management/actions";
import { TFactoryManagement } from '../../setting/factory/state_management/types';
import moment from 'moment';
import dayjs from 'dayjs';

type Props = {
    dispatch: Dispatcher;
    shipmentState: ShipmentState;
    appState: AppState;
};

const options = {
    chart: {
        height: "100%",
        type: 'line' as const,
        zoom: {
            enabled: false
        },
        toolbar: {
            show: false
        }
    },
    colors: ['#5F9FF2', '#FCB545', '#E0420F'],
    stroke: {
        curve: 'straight' as const
    },
    dataLabels: {
        enabled: false
    },
    grid: {
        borderColor: '#e7e7e7',
        row: {
            colors: ['#f3f3f3', 'transparent'],
            opacity: 0.5
        },
        padding: {
            top: 10,
            bottom: 0,
            right: 50,
        },
    },
    xaxis: {
        title: {
            text: '',
            offsetY: -10,
        },
        type: 'datetime' as const,
        tickPlacement: 'between',
        labels: {
            datetimeUTC: false,
            offsetY: 10,
            rotate: -45,
            rotateAlways: true,
            style: {
                fontSize: '11px',
            },
            formatter: function (val: any) {
                const m = moment(val);
                return m.isValid() ? m.format('HH:mm') : '';
            },
        },
    },
    yaxis: {
        title: {
            text: '打設量（m3）'
        },
        min: 0,
        labels: {
            formatter: function (val : any) {
                return val.toFixed(2);
            }
        }
    },
    legend: { show: false },
    markers: {
        size: 1,
        colors: undefined,
        strokeColors: 'transparent',
        hover: {
            size: 6,
            sizeOffset: 3
        }
    },
    tooltip: {
        enabled: true,
        shared: false,
        intersect: true,
        x: {
            show: true,
            formatter: (val: any) => moment(val).format('HH:mm')
        },
        y: {
            formatter: (val: any) => `${val} m³`
        }
    },
};

const ProcessChart: React.FC<Props> = ({ shipmentState, appState, dispatch }) => {
    const { dashboardChartExpanded } = appState;
    const locationId = localStorage.getItem('currentLocationId') || appState.currentLocationId || ''
    const { processChartData } = shipmentState;
    const [selectedFilter, setSelectedFilter] = useState("");
    const { factoryList, pumpList } = shipmentState;
    const truckList = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

    let periodTimesForTicks: Date[] | string[] | undefined = processChartData?.periodTimes;

    if (selectedFilter === 'PUMP' && processChartData?.finishedAmounts?.length) {
        const finished = processChartData.finishedAmounts;
        const firstTime = new Date(finished[0].time);
        const lastTime = new Date(finished[finished.length - 1].time);
        periodTimesForTicks = [firstTime, lastTime];
    }

    const getTickAmounts = (
        periodTimes?: string[] | Date[],
        maxTicks: number = 15
    ) => {

        if (!periodTimes || periodTimes.length < 2) {
            return { intervalMinutes: 0, tickCount: 0, start: null, end: null, isDaily: false };
        }
        let start = dayjs(periodTimes[0]);
        let end = dayjs(periodTimes[periodTimes.length - 1]);
        if (!start.isValid() || !end.isValid()) {
            return { intervalMinutes: 0, tickCount: 0, start: null, end: null, isDaily: false };
        }
        let diffMinutes = end.diff(start, 'minutes');
        if (diffMinutes <= 0) {
            return { intervalMinutes: 0, tickCount: 0, start, end, isDaily: false };
        }

        const ideal = diffMinutes / maxTicks;

        let intervalMinutes = 1440;
        if (ideal <= 1) intervalMinutes = 1;
        else if (ideal <= 5) intervalMinutes = 5;
        else if (ideal <= 10) intervalMinutes = 10;
        else if (ideal <= 30) intervalMinutes = 30;
        else if (ideal <= 60) intervalMinutes = 60;
        else if (ideal <= 180) intervalMinutes = 180;
        else if (ideal <= 360) intervalMinutes = 360;
        else if (ideal <= 720) intervalMinutes = 720;
        else intervalMinutes = 1440;

        const isDaily = intervalMinutes > 720;

        const roundDown = (d: dayjs.Dayjs, unit: number) => {
            const totalMinutes = d.hour() * 60 + d.minute();
            const rounded = Math.floor(totalMinutes / unit) * unit;
            return d.startOf("day").add(rounded, "minute");
        };

        const roundUp = (d: dayjs.Dayjs, unit: number) => {
            const totalMinutes = d.hour() * 60 + d.minute();
            const rounded = Math.ceil(totalMinutes / unit) * unit;
            return d.startOf("day").add(rounded, "minute");
        };

        const originalEnd = end.clone();

        if (isDaily) {
            start = start.startOf("day");
            end = end.endOf("day").add(1, "minute");
        } else {
            start = roundDown(start, intervalMinutes);
            end = roundUp(end, intervalMinutes);
            if (end.isSame(originalEnd)) {
                end = end.add(intervalMinutes, "minute");
            }
        }

        let tickCount: number;
        if (isDaily) {
            const diffDays = end.diff(start, "day");
            tickCount = diffDays;
        } else {
            diffMinutes = end.diff(start, "minutes");
            tickCount = Math.floor(diffMinutes / intervalMinutes);
        }

        return { tickCount, start, end, isDaily };
    };

    const { start, end, tickCount, isDaily } = getTickAmounts(periodTimesForTicks, 30);

    const optionFull = {
        ...options,
        xaxis: {
            ...options.xaxis,
            min: start ? start.valueOf() : undefined,
            max: end ? end.valueOf() : undefined,
            tickAmount: tickCount,
            labels: {
                formatter: (val: any) => {
                    const m = moment(val);
                    return isDaily ? m.format('YYYY-MM-DD') : m.format('HH:mm');
                }
            },
            rangePadding: "additional",
        },
        tooltip: {
            ...options.tooltip,
            x: {
                formatter: (val: any) => {
                    const m = moment(val);
                    return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : '';
                }
            },
            y: {
                formatter: (val: any) => {
                    if (val === null || val === undefined) return '-';
                    return `${val} m³`;
                }
            }
        }
    }

    const { start: start2, end: end2, tickCount: tickCount2, isDaily: isDaily2 } = getTickAmounts(periodTimesForTicks, 15);

    const optionLess = {
        ...options,
        xaxis: {
            ...options.xaxis,
            min: start2 ? start2.valueOf() : undefined,
            max: end2 ? end2.valueOf() : undefined,
            tickAmount: tickCount2,
            labels: {
                formatter: (val: any) => {
                    const m = moment(val);
                    return isDaily2 ? m.format('YYYY-MM-DD') : m.format('HH:mm');
                }
            },
            rangePadding: "additional",
        },
        tooltip: {
            ...options.tooltip,
            x: {
                formatter: (val: any) => {
                    const m = moment(val);
                    return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : '';
                }
            },
            y: {
                formatter: (val: any) => {
                    if (val === null || val === undefined) return '-';
                    return `${val} m³`;
                }
            }
        }
    }

    const optionPlantFull = {
        ...optionFull,
        colors: ['#5F9FF2', '#E0420F']
    }

    const optionPlantLess = {
        ...optionLess,
        colors: ['#5F9FF2', '#E0420F']
    }

    const optionPumpFull = {
        ...optionFull,
        colors: ['#5F9FF2']
    }

    const optionPumpLess = {
        ...optionLess,
        colors: ['#5F9FF2']
    }


    const series = [
        {
            name: '実打設',
            data: processChartData?.finishedAmounts?.map(p => ({
                x: moment(p.time).toDate(),
                y: p.value
            })) || []
        },
        {
            name: '計画打設量',
            data: (() => {
                if (!processChartData?.plannedVolume || !processChartData?.plannedAmounts?.length) return [];
                let firstTime = moment(processChartData.plannedAmounts[0].time).toDate();
                let lastTime = moment(processChartData.plannedAmounts[processChartData.plannedAmounts.length - 1].time).toDate();

                if (dashboardChartExpanded) {
                    firstTime = start?.toDate() ?? firstTime;
                    lastTime = end?.toDate() ?? lastTime;
                } else {
                    firstTime = start2?.toDate() ?? firstTime;
                    lastTime = end2?.toDate() ?? lastTime;
                }
                return [
                    { x: firstTime, y: processChartData.plannedVolume },
                    { x: lastTime, y: processChartData.plannedVolume }
                ];
            })()
        },
        {
            name: '打設予定量',
            data: processChartData?.plannedAmounts?.map(p => ({
                x: moment(p.time).toDate(),
                y: p.value
            })) || []
        },
    ]

    const filteredSeries = [
        {
            name: '実打設',
            data: processChartData?.finishedAmounts?.map(p => ({
                x: moment(p.time).toDate(),
                y: p.value
            })) || []
        },
        {
            name: '打設予定量',
            data: processChartData?.plannedAmounts?.map(p => ({
                x: moment(p.time).toDate(),
                y: p.value
            })) || []
        },
    ]

    const filteredPump = [
        {
            name: '実打設',
            data: processChartData?.finishedAmounts?.map(p => ({
                x: moment(p.time).toDate(),
                y: p.value
            })) || []
        },
    ]


    useEffect(() => {
        dispatch(shipmentActions.fetchFactoryList());
        dispatch(shipmentActions.fetchPumpList({ locationId }));
        if (locationId) {
            dispatch(shipmentActions.fetchProcessChartData({ locationId }));
            dispatch(shipmentActions.fetchOrderByLocation(locationId));
        }
    }, [selectedFilter, locationId]);

    const handleExpandProcessChart = () => {
        dispatch(appActions.setDashboardChartExpaned(!dashboardChartExpanded));
    }

    const handleChangeSelectedFilter = (value: string) => {
        setSelectedFilter(value);
    }

    const handleChangeFilterOption = (value: string) => {

        if (locationId) {
            if (selectedFilter === "FACTORY") {
                dispatch(shipmentActions.fetchProcessChartData({ locationId, factoryId: value }));
            } else if (selectedFilter === "TRUCK") {
                dispatch(shipmentActions.fetchProcessChartData({ locationId, truckCode: value }));
            } else if (selectedFilter === "PUMP") {
                dispatch(shipmentActions.fetchProcessChartData({ locationId, pumpId: value }));
            }
        }
    }

    const renderSeries = () => {
        let dataSeries: ApexAxisChartSeries = series;
        if (selectedFilter === 'FACTORY' && filteredSeries.length > 0) {
            dataSeries = filteredSeries;
        } else if (selectedFilter === 'PUMP' && filteredPump.length > 0) {
            dataSeries = filteredPump;
        }
        return dataSeries;
    }

    const renderOption = () => {
        let optionRender = optionFull;

        if (dashboardChartExpanded) {
            if (selectedFilter === 'FACTORY' && filteredSeries.length > 0) {
                optionRender = optionPlantFull;
            }
            else if (selectedFilter === 'PUMP' && filteredPump.length > 0) {
                optionRender = optionPumpFull;
            }

        } else {
            optionRender = optionLess;

            if (selectedFilter === 'FACTORY' && filteredSeries.length > 0) {
                optionRender = optionPlantLess;
            }
            else if (selectedFilter === 'PUMP' && filteredPump.length > 0) {
                optionRender = optionPumpLess;
            }
        }

        return optionRender;
    };

    const factoryIdsInLocation = new Set((shipmentState.orderByLocation?.details ?? []).map(d => String(d.factoryId)).filter(Boolean));

    const factoryListForLocation = (factoryList ?? []).filter(f => factoryIdsInLocation.has(String(f.id)));

    return (
        <>
            <div className="bg-warning text-center cursor-pointer" style={{ fontWeight: "bold", cursor: "pointer" }} onClick={handleExpandProcessChart}>進捗グラフ</div>
            <div className="row container-fluid mt-3">
                <div className="col-1" />
                <div className="col-5">
                    <select className="form-select chartFilter" onChange={(e) => handleChangeSelectedFilter(e.target.value)}>
                        <option value="">全体</option>
                        <option value="FACTORY">工場毎</option>

                        <option value="PUMP">ポンプ車毎</option>
                    </select>
                </div>
                <div className="col-5">
                    <select className="form-select chartFilter" onChange={(e) => { handleChangeFilterOption(e.target.value) }}>
                        <option value=""></option>
                        {
                            selectedFilter === "FACTORY" && factoryListForLocation.map((f: TFactoryManagement.FactoryGetAllModel) => (
                                <option key={f.id} value={f.id}>{f.name}</option>
                            ))                            
                        }

                        {
                            selectedFilter === "PUMP" && pumpList?.length && pumpList.map((p: any) => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))
                        }
                    </select>
                </div>
            </div>
            <div id="chart">
                {renderSeries().some(series => series.data.length > 0) ? (
                    <Chart options={renderOption()} series={renderSeries()} type="line" height={'100%'} />
                ) : (
                    <div></div>
                )}
            </div>
        </>
    )
}
export default connect((state: RootState) => ({
    shipmentState: state.shipment,
    appState: state.app
}))(ProcessChart);